import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Download, RotateCcw } from 'react-feather';
import { toast } from 'react-toastify';
import {
  TelephonyAgent,
  TelephonyCall,
  callIsRedFlagged,
  callRedFlagReason,
  telephonyApi,
  telephonyUserApi,
} from '../../../Telephony/telephonyApi';
import { getTelephonyAnalyticsScope, isOutSourcingTeam } from '../../../Telephony/roles';
import { hasEmailAccess, useEmailAccessConfig } from '../../../utils/emailAccessConfig';
import { isOutSourcingUser } from '../../../utils/permissions';
import { currentAgentId } from '../../../Telephony/TelephonyProvider';
import RecordingPlayer from '../../../Telephony/RecordingPlayer';
import { Page, PageHeader, Card, Button, Field, TextInput, SearchInput, Select, Toolbar, Table, Loader, EmptyState } from '../../../ui';
import { CallStatusBadge } from './callStatus';
import './telephony.scss';

// UI-only filter value, not a backend status: it is sent as status=NO_ANSWER plus
// direction=INBOUND, the same pair the table labels "MISSED INCOMING".
const MISSED_INCOMING = 'MISSED_INCOMING';

// Platform-side teamId filter value for calls with no team: inbound calls no
// agent was rung for (unknown caller, sticky agent gone or busy).
const NO_TEAM = 'none';

const STATUSES = ['', MISSED_INCOMING, 'COMPLETED', 'NOT_CONNECTED', 'NO_ANSWER', 'BUSY', 'AGENT_NOT_CONNECTED', 'FAILED', 'RINGING', 'BRIDGED'];

const STATUS_LABELS: Record<string, string> = {
  '': 'All statuses',
  [MISSED_INCOMING]: 'Missed incoming',
  // Display-only: the option value stays BRIDGED, which is what the backend filters on.
  BRIDGED: 'Connected (in progress)',
  AGENT_NOT_CONNECTED: 'Agent not connected',
};

const PAGE_SIZE = 50;
const CSV_BATCH_SIZE = 200;

/** Quote a CSV cell; a leading = + - @ is prefixed with ' so spreadsheets don't run it as a formula. */
const csvCell = (value: unknown): string => {
  const text = String(value ?? '');
  const safe = /^[=+\-@]/.test(text) && !/^[+-]?\d[\d.]*$/.test(text) ? `'${text}` : text;
  return `"${safe.replace(/"/g, '""')}"`;
};

const DirectionBadge: React.FC<{ direction?: string }> = ({ direction }) => {
  const inbound = direction === 'INBOUND';
  return (
    <i
      className={`fa ${inbound ? 'fa-arrow-down' : 'fa-arrow-up'} tel-dir ${inbound ? 'tel-dir--in' : 'tel-dir--out'}`}
      title={inbound ? 'Incoming call' : 'Outgoing call'}
      aria-label={inbound ? 'Incoming call' : 'Outgoing call'}
    />
  );
};

/**
 * Telephony → Logs: call history with recording playback. Every role except
 * ROLE_OUT_SOURCING gets the team/agent filters.
 *
 * The list is fetched as the signed-in user (telephonyUserApi), so the
 * platform can also scope it from the CMS token. See calls.controller.ts
 * resolveListScope.
 *
 * Outsourced agents on the "outSourcingAnalytics" list get the agent filter
 * limited to the outsourcing team (no team filter), same as Analytics. Every
 * other outsourced agent has no filters, and the list is always requested
 * with their own agentId — the platform does not scope ROLE_OUT_SOURCING by
 * token, so without it they would see the whole organisation's calls.
 */
const TelephonyLogsPage: React.FC = () => {
  const navigate = useNavigate();
  // Re-render once adminEmailAccess.json lands — the manager/outsourcing lists live there.
  useEmailAccessConfig();
  const outSourcing = getTelephonyAnalyticsScope() === 'outSourcing';
  const outSourcingSelf = isOutSourcingUser() && !outSourcing;
  // Team/agent filters for everyone except outsourced agents without "outSourcingAnalytics".
  const manager = !outSourcingSelf;
  // Platform agent id of the signed-in outsourced agent; '' = looked up, not enrolled.
  const [selfAgentId, setSelfAgentId] = useState<string | null>(null);
  const [calls, setCalls] = useState<TelephonyCall[]>([]);
  const [agents, setAgents] = useState<Record<string, string>>({});
  const [agentList, setAgentList] = useState<TelephonyAgent[]>([]);
  const [agentFilter, setAgentFilter] = useState('');
  const [teamFilter, setTeamFilter] = useState('');
  const [teamList, setTeamList] = useState<{ id: string; key: string; name: string }[]>([]);
  const [teamsLoaded, setTeamsLoaded] = useState(false);
  const [direction, setDirection] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [status, setStatus] = useState('');
  const [customer, setCustomer] = useState('');
  const [flaggedOnly, setFlaggedOnly] = useState(false);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);

  // A filter change invalidates whatever page we were on.
  useEffect(() => {
    setPage(0);
  }, [status, customer, agentFilter, teamFilter, direction, fromDate, toDate, flaggedOnly]);

  useEffect(() => {
    if (!outSourcingSelf) return;
    // Same match the softphone uses for enrolment (TelephonyProvider): agent.externalId === CMS uId.
    telephonyApi<TelephonyAgent[]>('/v1/agents')
      .then((list) => setSelfAgentId(list.find((a) => a.externalId === currentAgentId())?.id ?? ''))
      .catch(() => setSelfAgentId(''));
  }, [outSourcingSelf]);

  useEffect(() => {
    // Filter dropdowns only — outsourced agents without the list have no filters to fill.
    if (!manager) return;
    telephonyApi<{ id: string; key: string; name: string; isActive: boolean }[]>('/v1/teams')
      .then((teams) => setTeamList(teams.filter((t) => t.isActive)))
      .catch(() => undefined)
      .finally(() => setTeamsLoaded(true));
    telephonyApi<TelephonyAgent[]>('/v1/agents')
      .then((list) => {
        setAgents(Object.fromEntries(list.map((a) => [a.id, a.name])));
        setAgentList(list);
      })
      .catch(() => undefined);
  }, [manager]);

  // Outsourced agents are always pinned to their own team, so "All agents" never means the whole org.
  const outSourcingTeamId = outSourcing ? teamList.find(isOutSourcingTeam)?.id : undefined;

  // The filter set shared by the on-screen list and the CSV export, so a download
  // is always limited to exactly what the user's role and filters allow.
  const buildParams = useCallback((): URLSearchParams => {
    const params = new URLSearchParams();
    if (status === MISSED_INCOMING) {
      params.set('status', 'NO_ANSWER');
      params.set('direction', 'INBOUND');
    } else {
      if (status) params.set('status', status);
      if (direction) params.set('direction', direction);
    }
    if (customer.trim()) params.set('customerE164', customer.trim());
    if (outSourcingSelf && selfAgentId) params.set('agentId', selfAgentId);
    else if (manager && agentFilter) params.set('agentId', agentFilter);
    if (outSourcing && !agentFilter && outSourcingTeamId) params.set('teamId', outSourcingTeamId);
    else if (manager && !outSourcing && teamFilter) params.set('teamId', teamFilter);
    // Local start/end of day, sent as ISO timestamps.
    if (fromDate) params.set('from', new Date(`${fromDate}T00:00:00`).toISOString());
    if (toDate) params.set('to', new Date(`${toDate}T23:59:59.999`).toISOString());
    if (flaggedOnly) params.set('flagged', 'true');
    return params;
  }, [manager, outSourcing, outSourcingTeamId, outSourcingSelf, selfAgentId, status, customer, agentFilter, teamFilter, direction, fromDate, toDate, flaggedOnly]);

  const load = useCallback(() => {
    if (outSourcingSelf && !selfAgentId) {
      // Never list unscoped: wait for the agent lookup, and show nothing if they aren't enrolled.
      if (selfAgentId === '') {
        setCalls([]);
        setTotal(0);
        setLoading(false);
      }
      return;
    }
    if (outSourcing && !outSourcingTeamId) {
      // Don't list unscoped while /v1/teams is still loading; say so if it has no outsourcing team.
      if (teamsLoaded) {
        setCalls([]);
        setTotal(0);
        setLoading(false);
        toast.error('Logs: no outsourcing team found in Telephony → Teams.');
      }
      return;
    }
    const params = buildParams();
    params.set('take', String(PAGE_SIZE));
    params.set('skip', String(page * PAGE_SIZE));
    setLoading(true);
    telephonyUserApi<{ items: TelephonyCall[]; total: number }>(`/v1/calls?${params}`)
      .then((res) => {
        setCalls(res.items);
        setTotal(res.total);
      })
      .catch((e) => toast.error(`Logs: ${e.message}`))
      .finally(() => setLoading(false));
  }, [buildParams, outSourcing, outSourcingTeamId, teamsLoaded, outSourcingSelf, selfAgentId, page]);

  useEffect(load, [load]);

  // CSV download is allowlisted via "telephonyLogsCsv" in adminEmailAccess.json.
  const canExportCsv = hasEmailAccess('telephonyLogsCsv');
  const [exporting, setExporting] = useState(false);

  const exportCsv = async () => {
    // Same guards as load(): never export unscoped.
    if ((outSourcingSelf && !selfAgentId) || (outSourcing && !outSourcingTeamId)) return;
    setExporting(true);
    try {
      const rows: TelephonyCall[] = [];
      let fetched = 0;
      let count = 0;
      do {
        const params = buildParams();
        params.set('take', String(CSV_BATCH_SIZE));
        params.set('skip', String(fetched));
        const res = await telephonyUserApi<{ items: TelephonyCall[]; total: number }>(`/v1/calls?${params}`);
        rows.push(...res.items);
        fetched += res.items.length;
        count = res.total;
        if (!res.items.length) break;
      } while (fetched < count);

      const header = ['Time', 'Agent', 'Agent ID', 'Customer ID', 'Direction', 'Channel', 'From', 'To', 'Status', 'Hangup cause', 'Talk time (sec)', 'Vendor', 'Source'];
      const lines = rows.map((c) => [
        new Date(c.createdAt).toLocaleString(),
        c.agent?.name ?? (c.agentId ? agents[c.agentId] : '') ?? '',
        c.agent?.externalId ?? '',
        c.customerUserId ?? '',
        c.direction,
        c.channel ?? '',
        c.direction === 'INBOUND' ? c.customerE164 : c.callerIdE164,
        c.direction === 'INBOUND' ? c.callerIdE164 : c.customerE164,
        c.status,
        c.hangupCause ?? '',
        c.talkSeconds ?? '',
        c.provider,
        c.source,
      ]);
      const csv = [header, ...lines].map((r) => r.map(csvCell).join(',')).join('\r\n');
      const url = URL.createObjectURL(new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `telephony-logs-${new Date().toISOString().slice(0, 10)}.csv`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(`Logs CSV: ${(e as Error).message}`);
    } finally {
      setExporting(false);
    }
  };


  const statusOptions = STATUSES.map((s) => ({ value: s, label: STATUS_LABELS[s] ?? s }));
  const directionOptions = [
    { value: '', label: 'All calls' },
    { value: 'INBOUND', label: 'Incoming' },
    { value: 'OUTBOUND', label: 'Outgoing' },
  ];
  const teamOptions = [
    { value: '', label: 'All teams' },
    ...teamList.map((t) => ({ value: t.id, label: t.name })),
    { value: NO_TEAM, label: 'No team / Unassigned' },
  ];
  const agentOptions = [
    { value: '', label: 'All agents' },
    ...agentList
      .filter((a) => !outSourcing || isOutSourcingTeam(a.team))
      .filter(
        (a) =>
          !teamFilter ||
          teamFilter === NO_TEAM ||
          a.team?.key === teamList.find((t) => t.id === teamFilter)?.key,
      )
      .map((a) => ({ value: a.id, label: a.name })),
  ];

  return (
    <Page>
      <PageHeader
        eyebrow="Telephony"
        title="Call logs"
        subtitle={loading ? 'Loading calls…' : `${total} calls match`}
        actions={
          <>
            <Button variant="secondary" icon={<RotateCcw size={16} aria-hidden />} onClick={load}>
              Refresh
            </Button>
            {canExportCsv ? (
              <Button
                variant="secondary"
                icon={<Download size={16} aria-hidden />}
                loading={exporting}
                disabled={loading}
                onClick={() => void exportCsv()}
              >
                {exporting ? 'Preparing…' : 'Download CSV'}
              </Button>
            ) : null}
          </>
        }
      />

      <Card>
        <Toolbar>
          <Field label="Customer number" className="ds-toolbar__grow">
            {(ctl) => (
              <SearchInput
                {...ctl}
                placeholder="Customer number (+91…)"
                value={customer}
                onChange={(e) => setCustomer(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && load()}
              />
            )}
          </Field>
          <Field label="Status">
            {(ctl) => (
              <Select
                {...ctl}
                options={statusOptions}
                value={status}
                onChange={(e) => {
                  setStatus(e.target.value);
                  // keep the direction dropdown in step with what is actually queried
                  if (e.target.value === MISSED_INCOMING) setDirection('INBOUND');
                }}
              />
            )}
          </Field>
          <Field label="Direction">
            {(ctl) => (
              <Select
                {...ctl}
                options={directionOptions}
                value={direction}
                onChange={(e) => {
                  setDirection(e.target.value);
                  // missed incoming only exists for incoming calls
                  if (status === MISSED_INCOMING && e.target.value !== 'INBOUND') setStatus('');
                }}
              />
            )}
          </Field>
          {manager && !outSourcing && (
            <Field label="Team">
              {(ctl) => (
                <Select
                  {...ctl}
                  options={teamOptions}
                  value={teamFilter}
                  onChange={(e) => { setTeamFilter(e.target.value); setAgentFilter(''); }}
                />
              )}
            </Field>
          )}
          {manager && (
            <Field label="Agent">
              {(ctl) => (
                <Select {...ctl} options={agentOptions} value={agentFilter} onChange={(e) => setAgentFilter(e.target.value)} />
              )}
            </Field>
          )}
          <Field label="From">
            {(ctl) => <TextInput {...ctl} type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />}
          </Field>
          <Field label="To">
            {(ctl) => <TextInput {...ctl} type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />}
          </Field>
          <div className="ds-toolbar__end">
            <Button
              variant={flaggedOnly ? 'accent' : 'secondary'}
              aria-pressed={flaggedOnly}
              onClick={() => setFlaggedOnly((v) => !v)}
              title="Show only calls flagged during automated review"
            >
              🚩 Flagged only
            </Button>
          </div>
        </Toolbar>
      </Card>

      <Card flush>
        {loading ? (
          <Loader label="Loading calls…" />
        ) : calls.length === 0 ? (
          <div className="tel-flush-pad">
            <EmptyState title="No calls match" description="Try widening the dates or clearing the status, team and agent filters." />
          </div>
        ) : (
          <Table caption="Call logs">
            <thead>
              <tr>
                {/* Flag, Recording and View are the columns people actually act on, so
                    they stay leftmost and visible without scrolling; everything after
                    Agent is reference detail that's fine to scroll to. */}
                <th className="tel-flag-col"><span className="visually-hidden">Flag</span></th>
                <th>Recording</th>
                <th><span className="visually-hidden">Actions</span></th>
                <th>Time</th>
                <th>Agent</th>
                <th>Customer ID</th>
                <th>From → To</th>
                <th>Status</th>
                <th className="ds-num">Talk time</th>
                <th>Vendor</th>
                <th>Source</th>
              </tr>
            </thead>
            <tbody>
              {calls.map((c) => {
                const stored = c.recordings.find((r) => r.status === 'STORED');
                const redFlagged = callIsRedFlagged(c);
                const redFlagReason = callRedFlagReason(c);
                return (
                  <tr key={c.id}>
                    <td className="tel-flag-col">
                      {redFlagged ? (
                        <span
                          title={redFlagReason ?? 'Flagged for review'}
                          aria-label="Flagged for review"
                        >
                          🚩
                        </span>
                      ) : null}
                    </td>
                    <td>
                      {stored ? (
                        <RecordingPlayer recordingId={stored.id} />
                      ) : c.recordings.length ? (
                        <span className="ds-text-subtle">{c.recordings[0].status.toLowerCase()}</span>
                      ) : (
                        <span className="ds-text-subtle">—</span>
                      )}
                    </td>
                    <td className="text-nowrap">
                      <Button
                        size="sm"
                        variant={redFlagged ? 'accent' : 'secondary'}
                        onClick={() =>
                          navigate(`${process.env.PUBLIC_URL}/pages/telephony-logs/${c.id}`)
                        }
                      >
                        View
                      </Button>
                    </td>
                    <td className="text-nowrap">{new Date(c.createdAt).toLocaleString()}</td>
                    <td>
                      {c.agent?.name ?? (c.agentId ? agents[c.agentId] : null) ?? '—'}
                      {c.agent?.externalId ? (
                        <span className="tel-sub">
                          <code>{c.agent.externalId}</code>
                        </span>
                      ) : null}
                    </td>
                    <td>
                      {c.customerUserId ? <code>{c.customerUserId}</code> : <span className="ds-text-subtle">—</span>}
                    </td>
                    <td className="text-nowrap">
                      <DirectionBadge direction={c.direction} />
                      {c.channel === 'WHATSAPP' ? (
                        <i
                          className="fa fa-whatsapp tel-whatsapp-icon me-1"
                          title="WhatsApp call"
                          aria-label="WhatsApp call"
                        />
                      ) : null}
                      {c.direction === 'INBOUND'
                        ? <>{c.customerE164} → {c.callerIdE164}</>
                        : <>{c.callerIdE164} → {c.customerE164}</>}
                    </td>
                    <td>
                      <CallStatusBadge status={c.status}>
                        {c.direction === 'INBOUND' && c.status === 'NO_ANSWER'
                          ? 'MISSED INCOMING'
                          : c.status === 'NOT_CONNECTED'
                          ? 'NOT CONNECTED (VOICEMAIL)'
                          : c.status === 'BRIDGED'
                          ? 'CONNECTED (IN PROGRESS)'
                          : c.status}
                      </CallStatusBadge>
                      {c.hangupCause ? <span className="tel-sub">{c.hangupCause}</span> : null}
                      {c.disconnectedBy ? (
                        <span className="tel-sub">Ended by {c.disconnectedBy.toLowerCase()}</span>
                      ) : null}
                    </td>
                    <td className="ds-num">{c.talkSeconds != null ? `${c.talkSeconds}s` : '—'}</td>
                    <td>{c.provider}</td>
                    <td>
                      {c.source}
                      {c.sourceRecordId ? <span className="tel-sub">{c.sourceRecordId}</span> : null}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        )}

        <div className="tel-pager tel-flush-pad">
          <span className="ds-text-subtle ds-num">
            {total > 0
              ? `Showing ${page * PAGE_SIZE + 1}–${Math.min((page + 1) * PAGE_SIZE, total)} of ${total}`
              : null}
          </span>
          <div className="tel-pager__buttons">
            <Button
              size="sm"
              variant="secondary"
              disabled={loading || page === 0}
              onClick={() => setPage((p) => Math.max(0, p - 1))}
            >
              Previous
            </Button>
            <Button
              size="sm"
              variant="secondary"
              disabled={loading || (page + 1) * PAGE_SIZE >= total}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      </Card>
    </Page>
  );
};

export default TelephonyLogsPage;
